import { storagePutContract, type DatasetStorageBackend } from "@/api/datasetStorageBackend";
import type { ExtraFileWriteApi } from "./extraFilesApi";

// 선택 폴더(parent) 아래 상대 경로. parent 가 없으면 root 바로 아래다(파일명 그대로).
export function extraFileRelativePath(parent: string | undefined, fileName: string): string {
  return parent ? `${parent}/${fileName}` : fileName;
}

// 이름이 NFC 인 File 을 반환한다. 이름이 이미 NFC 면 같은 객체를 반환한다. 내용은 복사하지 않는다.
function withNfcName(file: File): File {
  const name = file.name.normalize("NFC");
  return name === file.name
    ? file
    : new File([file], name, { type: file.type, lastModified: file.lastModified });
}

/**
 * 이미 고른 파일에 새로 고른 파일을 추가한다(선택 버튼과 드래그앤드롭이 같이 쓴다).
 *
 * 이름이 같으면 나중 파일로 대체한다. 대상 폴더가 하나이므로 이름이 같으면 경로도 같다.
 * 서버는 한 요청 안의 중복 경로를 409 로 거부한다. 그래서 두 번 고른 파일이 목록에 두 줄로
 * 남지 않게 여기서 정리한다.
 * 순서는 처음 고른 위치를 유지한다. 그래서 목록 순서가 바뀌지 않는다.
 *
 * 고른 파일의 이름은 유니코드 NFC 로 바꾼다. macOS 의 파일 이름은 NFD 일 수 있다. 서버는 본문에
 * NFC 가 아닌 문자열이 있으면 요청을 422 로 거부한다. 다이얼로그는 이 이름으로 다음 세 가지를 한다.
 * - 선언할 경로를 만든다.
 * - 대상 폴더에서 같은 이름의 파일을 조회한다.
 * - 덮어씀을 표시한다.
 */
export function mergePickedFiles(
  current: readonly File[],
  incoming: readonly File[],
): readonly File[] {
  const merged = [...current];
  for (const picked of incoming) {
    const file = withNfcName(picked);
    const index = merged.findIndex((existing) => existing.name === file.name);
    if (index >= 0) {
      merged[index] = file;
    } else {
      merged.push(file);
    }
  }
  return merged;
}

// `alreadyExists` 는 `failed` 중에서 "이미 있는 경로"의 status 로 거절된 수다. 그 status 가 다른 사유와
// 겹치는 저장 위치(`existsStatusAmbiguous`)에서는 이미 있는 경로가 아닐 수도 있다.
export type ExtraFileUploadResult = { uploaded: number; failed: number; alreadyExists: number };

// 선택 폴더(parent) 아래로 extra 파일을 업로드한다. 순서는 다음과 같다:
//   1. declare(서버 ticket)
//   2. client-direct PUT
//   3. finalize(검증·등록)
// write seam(declare/finalize)이 없으면 아무것도 하지 않는다. 일부 PUT 이 실패하면 성공한 파일만
// finalize 하고 {uploaded, failed, alreadyExists} 로 알린다. 호출부가 결과로 안내와 재조회를 처리한다.
//
// 새 경로의 티켓은 object 가 없을 때만 쓸 수 있다. 카탈로그가 모르는 object(옛 반입의 잔재)나 다른
// 사람의 동시 업로드가 있으면 storage 가 거절한다. 그 status 는 저장 위치마다 다르다. 저장 위치를
// 모르면 GCS 의 값으로 판정한다.
//
// 선택 파일은 배열로 받는다. 업로드 다이얼로그는 고른 목록을 보여 주고, 사용자는 항목을 지우고
// 다시 고를 수 있다. 그래서 input 이 준 FileList 를 그대로 보관하지 않는다.
export async function uploadExtraFilesToFolder(
  api: Pick<ExtraFileWriteApi, "declare" | "finalize">,
  uuid: string,
  parent: string | undefined,
  files: readonly File[],
  storageBackend?: DatasetStorageBackend,
): Promise<ExtraFileUploadResult> {
  if (!api.declare || !api.finalize || files.length === 0) {
    return { uploaded: 0, failed: 0, alreadyExists: 0 };
  }
  const { existsStatus } = storagePutContract(storageBackend);
  const picked = [...files];
  const byPath = new Map(picked.map((file) => [extraFileRelativePath(parent, file.name), file]));
  const declared = await api.declare(uuid, {
    files: picked.map((file) => ({
      relative_path: extraFileRelativePath(parent, file.name),
    })),
  });
  // 파일마다 결과를 값으로 모은다. `path` 는 PUT 이 성공한 경로다. 거절되면 null 이다.
  const results = await Promise.allSettled(
    declared.files.map(async (ticket) => {
      const blob = byPath.get(ticket.relative_path);
      if (!blob) throw new Error(`Missing file for ${ticket.relative_path}`);
      // 티켓의 headers 는 그대로 보내야 한다. GCS 는 헤더가 서명에 포함된다. 빠뜨리면 서명 불일치로
      // 403 이다. Azure 는 `x-ms-blob-type` 이 없으면 400 이다.
      const response = await fetch(ticket.upload_url, {
        method: ticket.method,
        headers: ticket.headers,
        body: blob,
      });
      if (response.ok) {
        return { path: ticket.relative_path, alreadyExists: false };
      }
      // replace 티켓에는 "없을 때만" 조건이 없다. 같은 status 여도 다른 사유다.
      return {
        path: null,
        alreadyExists: ticket.replaces !== true && response.status === existsStatus,
      };
    }),
  );
  const outcomes = results.flatMap((result) =>
    result.status === "fulfilled" ? [result.value] : [],
  );
  const uploadedPaths = outcomes.flatMap((outcome) =>
    outcome.path === null ? [] : [outcome.path],
  );
  if (uploadedPaths.length > 0) {
    await api.finalize(uuid, { relative_paths: uploadedPaths });
  }
  return {
    uploaded: uploadedPaths.length,
    failed: results.length - uploadedPaths.length,
    alreadyExists: outcomes.filter((outcome) => outcome.alreadyExists).length,
  };
}
